import { useCallback, useEffect, useState } from 'react';

import { ChildDetails, DEFAULT_CANDIDATE_DETAILS, ForWhom, RegistrationCandidateDetails } from 'store/candidate/types';
import { selectCandidate } from 'store/candidate/candidateSlice';
import { useAppSelector } from 'store';
import { useLazyGetCandidateQuery, useLazyGetUsersCandidateQuery } from 'store/candidate/candidateAPI';
import { NativeLangEnum, NativeLangOptions } from 'core';

export const useCandidateInitialValues = () => {
  const storedCandidate = useAppSelector(selectCandidate);
  const [getUserCandidate, { data: userCandidate, isFetching: userLoading, isSuccess: userLoaded }] =
    useLazyGetUsersCandidateQuery();
  const [getChildCandidate, { data: childCandidate, isFetching: childLoading, isSuccess: childLoaded }] =
    useLazyGetCandidateQuery();

  const [initialValues, setInitialValues] = useState({
    ...DEFAULT_CANDIDATE_DETAILS,
    ...storedCandidate,
    childDetails: getChildDetails(storedCandidate?.childDetails),
  });

  const updateInitialValues = (details: Partial<RegistrationCandidateDetails>) => {
    setInitialValues({
      ...DEFAULT_CANDIDATE_DETAILS,
      ...details,
      childDetails: getChildDetails(details?.childDetails),
      //[GP]: we have to override default value because API return "0" value as a default....
      nativeLanguage:
        details.nativeLanguage && !!NativeLangOptions.find((x) => x.id === details.nativeLanguage)
          ? details.nativeLanguage
          : NativeLangEnum.Unknown,
    });
  };

  const onCandidateChange = useCallback(
    (forWhom: ForWhom | undefined, id: number | null) => {
      switch (forWhom) {
        case ForWhom.Myself:
          getUserCandidate();
          break;
        case ForWhom.CurrentChild:
          getChildCandidate(id);
          break;
        default:
          updateInitialValues({ forWhom, isChild: true });
      }
    },
    [getUserCandidate, getChildCandidate]
  );

  useEffect(() => {
    if (userLoaded && !userLoading) {
      updateInitialValues({
        ...userCandidate,
        forWhom: ForWhom.Myself,
      });
    }
  }, [userLoaded, userLoading, userCandidate]);

  useEffect(() => {
    if (childLoaded && !childLoading) {
      updateInitialValues({
        ...childCandidate,
        forWhom: ForWhom.CurrentChild,
      });
    }
  }, [childLoaded, childLoading, childCandidate]);

  return {
    initialValues,
    onCandidateChange,
    isLoading: userLoading || childLoading,
  };
};

function getChildDetails(model?: ChildDetails): ChildDetails{
  return {
    ...DEFAULT_CANDIDATE_DETAILS.childDetails,
    ...model,
    allowedPeople: model?.allowedPeople ?? DEFAULT_CANDIDATE_DETAILS.childDetails.allowedPeople,
  }
}
